import React from 'react';
import { Sparkles, Palette, ScanLine } from 'lucide-react';

// Visual-first welcome — DRAFT for ?preview=welcome-b.
// Same three CTAs as WelcomeScreen.jsx (onCreateAccount/onSignIn/onContinueAsGuest)
// but reframed around the three hero capabilities the demo opens on:
//   1. Skin Analysis  2. Shade Match  3. Product Scan
//
// Approach borrowed from the Lovi inspiration: light editorial canvas, a small
// wordmark, one warm serif greeting, then three tactile feature tiles. CTAs
// sit at the bottom so the eye lands on capabilities first.

const SERIF = '"DM Sans", system-ui, sans-serif';
const SANS = '"DM Sans", system-ui, sans-serif';

// Tile spec — easy to tweak copy without restructuring layout.
const FEATURES = [
  {
    key: 'skin',
    title: 'Skin Analysis',
    blurb: 'See your skin type, tone and concerns from one selfie.',
    Icon: Sparkles,
    bg: 'linear-gradient(135deg, #F4D9C5 0%, #E8B89A 100%)',
    iconColor: '#7A1F1F',
  },
  {
    key: 'shade',
    title: 'Shade Match',
    blurb: 'Find your exact tone — foundation, lipstick, blush.',
    Icon: Palette,
    bg: 'linear-gradient(135deg, #E9C6CE 0%, #C98B96 100%)',
    iconColor: '#5B1A2A',
  },
  {
    key: 'product',
    title: 'Product Scan',
    blurb: 'Scan any product. See how it fits your skin in seconds.',
    Icon: ScanLine,
    bg: 'linear-gradient(135deg, #E6DCC9 0%, #C5B59A 100%)',
    iconColor: '#3D2A1A',
  },
];

export default function WelcomeScreenB({ onSignIn, onCreateAccount, onContinueAsGuest }) {
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100dvh',
        width: '100%',
        background: '#F2EAE1',
        fontFamily: SANS,
      }}
    >
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          padding: '22px 24px calc(24px + env(safe-area-inset-bottom, 16px))',
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        {/* Wordmark */}
        <div style={{ textAlign: 'center', paddingTop: 4 }}>
          <span
            style={{
              color: '#2A1212',
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontSize: 22,
              letterSpacing: '0.01em',
            }}
          >
            Son:mi
          </span>
        </div>

        {/* Greeting */}
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <h1
            style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 34,
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
              color: '#2A1212',
              margin: 0,
            }}
          >
            Where would you<br />like to start?
          </h1>
          <p
            style={{
              fontFamily: SANS,
              fontSize: 13,
              lineHeight: 1.5,
              color: '#5B4040',
              marginTop: 10,
              marginBottom: 0,
              opacity: 0.82,
            }}
          >
            Skincare and makeup, in conversation.
          </p>
        </div>

        {/* Feature tiles */}
        <div
          style={{
            marginTop: 28,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {FEATURES.map(({ key, title, blurb, Icon, bg, iconColor }) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 16px',
                background: '#FFFFFF',
                borderRadius: 20,
                border: '1px solid rgba(42, 18, 18, 0.06)',
                boxShadow: '0 1px 2px rgba(42, 18, 18, 0.04)',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={22} color={iconColor} strokeWidth={1.6} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: SANS,
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#2A1212',
                    letterSpacing: '-0.005em',
                  }}
                >
                  {title}
                </div>
                <div
                  style={{
                    fontFamily: SANS,
                    fontSize: 13.5,
                    color: '#5B4040',
                    lineHeight: 1.4,
                    marginTop: 2,
                    opacity: 0.85,
                  }}
                >
                  {blurb}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs — pinned to bottom */}
        <div style={{ marginTop: 'auto', paddingTop: 28 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              type="button"
              onClick={onCreateAccount}
              style={{
                background: '#1F1310',
                color: '#F7F1EA',
                border: 'none',
                borderRadius: 999,
                padding: '16px 24px',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontFamily: SANS,
                cursor: 'pointer',
              }}
            >
              Create your account
            </button>

            <button
              type="button"
              onClick={onSignIn}
              style={{
                background: 'transparent',
                color: '#2A1212',
                border: '1px solid #C9B8A4',
                borderRadius: 999,
                padding: '15px 24px',
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontFamily: SANS,
                cursor: 'pointer',
              }}
            >
              Sign in
            </button>

            <button
              type="button"
              onClick={onContinueAsGuest}
              style={{
                background: 'transparent',
                color: '#5B4040',
                border: 'none',
                padding: '10px 0 2px',
                fontSize: 17,
                fontFamily: SERIF,
                fontStyle: 'italic',
                cursor: 'pointer',
              }}
            >
              Continue as guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
